/**
 * The pure logic behind the Predictions screens — no React, no data layer, so
 * it can be unit-tested directly. Same split as `utils/matchplay.js`.
 *
 * Every function here is about DIRECTION or SIGN, which is where this feature's
 * bugs live: the server's own "is Baz up on me overall" expression shipped
 * inverted the first time and only a test caught it.
 */
import { FORMAT_LABELS, KIND_LABELS } from './primitives';

/**
 * Everything one LOGGED row says, as plain values for the screen to lay out:
 *
 *   heading  "Head to head · Nett" — WHAT the bet is, first. SCOPE LEADS when
 *            the bet is on the whole competition, because that is the fact
 *            most likely to be misread: on a round screen an overall bet
 *            otherwise looks like a bet on the day. A sweep adds how its pot
 *            is paid.
 *   sides    [['Alice Ash'], ['Baz Bailey']] for a head to head or pairs, the
 *            caller's side first; null for a sweep.
 *   entrants every name in a sweep; null otherwise.
 *   status   'Just logged' / 'Settled up' / 'Halved' / 'Void — no card', or
 *            null while it is simply open.
 *
 * FULL NAMES, NEVER "You". This list is read by whoever is running the day,
 * who is logging bets for other people as often as for themselves — and it
 * is the record people check before they pay. A row that names nobody (the
 * old sweep row) or names "You" to an organiser who is not in it is a row
 * nobody can check.
 */
export const loggedView = (entry, myPlayerId, justLoggedId) => {
  const kind = KIND_LABELS[entry.kind] || entry.kind;
  const format = FORMAT_LABELS[entry.format] || entry.format;
  const heading = [entry.scope === 'overall' ? 'Overall' : null, kind, format];
  const names = (side) => (side.players || []).map((p) => p.name || 'Unknown');

  let sides = null;
  let entrants = null;
  if (entry.kind === 'sweep') {
    const places = Number(entry.config?.places_paid) || 1;
    heading.push(places > 1 ? `Top ${places} paid` : 'Winner takes all');
    entrants = (entry.sides || []).flatMap(names);
  } else {
    const all = entry.sides || [];
    const mine = all.findIndex((side) =>
      (side.players || []).some((p) => p.player_id === myPlayerId));
    const ordered = mine > 0 ? [all[mine], ...all.filter((_, i) => i !== mine)] : all;
    sides = ordered.map(names);
  }

  let status = null;
  if (justLoggedId != null && entry.id === justLoggedId) status = 'Just logged';
  else if (entry.status === 'resolved') status = entry.debts?.length ? 'Settled up' : 'Halved';
  else if (entry.status === 'void') status = 'Void — no card';

  return {
    heading: heading.filter(Boolean).join(' · '),
    sides,
    entrants,
    status,
  };
};

/** Everyone entered in a sweep (side-less rows), or nobody. */
const sweepEntrants = (entry) =>
  (entry.sides || []).reduce((n, side) => n + (side.players || []).length, 0);

/**
 * "You v Baz" from the sides, with the caller always named first.
 *
 * A SWEEP IS NOT A LIST OF NAMES. It enters the whole field, so joining its
 * entrants with "&" printed twelve names into one row and cut them off after
 * three — which read as a three-man bet.
 */
export const entryTitle = (entry, myPlayerId) => {
  if (entry.kind === 'sweep') {
    const n = sweepEntrants(entry);
    return `Sweep · ${n} ${n === 1 ? 'player' : 'players'}`;
  }
  const sides = entry.sides || [];
  const names = (side) => side.players.map((p) =>
    p.player_id === myPlayerId ? 'You' : p.name).join(' & ');
  if (sides.length !== 2) {
    return sides.length ? names(sides[0]) : 'Prediction';
  }
  const mine = sides.findIndex((side) =>
    side.players.some((p) => p.player_id === myPlayerId));
  if (mine === -1) return `${names(sides[0])} v ${names(sides[1])}`;
  const other = mine === 0 ? 1 : 0;
  return `${names(sides[mine])} v ${names(sides[other])}`;
};

/**
 * The figure on a logged row, and what it is a figure OF. A head to head or a
 * pairs entry is a stake per man; a sweep's `stake` is the entry per player,
 * so the row shows the POT — the number people talk about — and says what
 * each put in beneath it.
 */
export const entryAmount = (entry) => {
  const stake = Number(entry.stake) || 0;
  if (entry.kind === 'sweep') {
    return { amount: stake * sweepEntrants(entry), each: stake, label: 'pot' };
  }
  return { amount: stake, each: stake, label: 'each' };
};

/**
 * An OPEN entry already logged on these terms, or null.
 *
 * A sweep is the case this exists for: it has no slots to clear, so after a
 * confirm the screen used to look exactly as it did before, and the natural
 * reading was "that didn't go through" — so it was confirmed again. Matching
 * is on everything a person chooses (kind, scoring, stake, scope, and the
 * sides or the places paid), so a nett and a gross sweep on the same day are
 * two bets, not a duplicate. Sides match in either order: Baz v Alice is the
 * same match as Alice v Baz.
 */
export const findDuplicate = (entries, draft) => {
  const ids = (list) => [...list].map(Number).sort((a, b) => a - b).join(',');
  const sidesKey = (sides) => sides.map(ids).sort().join(' v ');
  const wanted = draft.kind === 'sweep' ? null : sidesKey(draft.sides || []);
  return (entries || []).find((entry) => {
    if (entry.status !== 'open' || entry.kind !== draft.kind) return false;
    if (entry.format !== draft.format) return false;
    if (Number(entry.stake) !== Number(draft.stake)) return false;
    if ((entry.round_number ?? null) !== (draft.roundNumber ?? null)) return false;
    if (draft.kind === 'sweep') {
      const places = (e) => Number(e?.places_paid) || 1;
      return places(entry.config) === places({ places_paid: draft.placesPaid });
    }
    const theirs = sidesKey((entry.sides || [])
      .map((side) => (side.players || []).map((p) => p.player_id)));
    return theirs === wanted;
  }) || null;
};

/**
 * What `rowId` owes `colId`, from the wire form ("low:high" -> what low owes
 * high). Returns 0 when nothing is owed that way round, INCLUDING when the debt
 * runs the other way: the grid is headed ROW PAYS COLUMN, so only one of a
 * pair's two cells is ever filled and the mirror stays an em dash.
 */
export const owed = (pairs, rowId, colId) => {
  const [low, high] = rowId <= colId ? [rowId, colId] : [colId, rowId];
  const amount = pairs[`${low}:${high}`];
  if (amount == null) return 0;
  const rowOwes = rowId === low ? amount : -amount;
  return rowOwes > 0 ? rowOwes : 0;
};

/** A grid cell's class, by what is owed. The £30 step is the handoff's. */
export const cellClass = (amount, isSelf) => {
  if (isSelf) return 'pr-matrix-cell pr-matrix-cell--self';
  if (!amount) return 'pr-matrix-cell';
  if (amount >= 30) return 'pr-matrix-cell pr-matrix-cell--high';
  return 'pr-matrix-cell pr-matrix-cell--low';
};

/**
 * The confirm sheet's shots line. Only meaningful for nett and matchplay — a
 * gross comparison gives nobody anything.
 *
 * Returns null when either handicap is missing rather than guessing. A player
 * with no handicap on file is one of the states the handoff asks us to design
 * for, and a confident "gets 0 shots" would be a lie.
 */
export const shotsLine = (me, them, format) => {
  if (format !== 'nett' && format !== 'matchplay') return null;
  if (me?.handicap == null || them?.handicap == null) return null;
  const shots = Math.round(Math.abs(them.handicap - me.handicap));
  if (shots === 0) return `${them.name} plays off the same handicap — no shots`;
  const theyGet = them.handicap > me.handicap;
  const receiver = theyGet ? them.name : 'You';
  const off = theyGet ? them.handicap : me.handicap;
  return `${receiver} plays off ${Math.round(off)} — gets ${shots} shot${
    shots === 1 ? '' : 's'
  }`;
};

/**
 * The name to use where the space is one fifth of a phone: the first word.
 *
 * The handoff's tiles and its matrix row labels are drawn with nicknames — Baz,
 * Daz, Matt, Oli — but this app stores whole names ("Baz Bailey"), and measured
 * in a real browser a 12px full name truncates in a 72px tile at EVERY phone
 * width. A truncated "Bolbo Bagg…" is worse than "Bolbo": it costs a character
 * and gains nothing.
 *
 * Two members sharing a first name therefore read alike in those two places.
 * That is the handoff's own trade (its column headers are bare initials, which
 * collide sooner) and both surfaces lead to a sheet that names the player in
 * full.
 */
export const shortName = (name) => (name || '').split(/\s+/)[0] || '';

/**
 * The balance against one player, from the hub's `pairs` map. Positive means
 * YOU owe THEM, matching the server's own convention so the two cannot drift.
 */
export const balanceAgainst = (pairs, myPlayerId, playerId) => {
  if (!pairs || myPlayerId == null) return 0;
  const [low, high] = myPlayerId <= playerId
    ? [myPlayerId, playerId] : [playerId, myPlayerId];
  const amount = pairs[`${low}:${high}`];
  if (amount == null) return 0;
  return myPlayerId === low ? amount : -amount;
};

/**
 * Default pot split per place — the server's own figures.
 *
 * Round numbers on purpose: a sweep is settled in a bar, and 60/40 and
 * 50/30/20 are the splits people say out loud.
 */
/** Mirrors `bet_resolution.DEFAULT_SPLITS` — what a place count pays when
 *  the society has not set its own shares. */
export const DEFAULT_SPLITS = {
  1: [100],
  2: [60, 40],
  3: [50, 30, 20],
  4: [40, 30, 20, 10],
  5: [35, 25, 20, 15, 5],
};

export const MAX_PLACES = 5;

/**
 * The split editor's rows: percentage and money per paid place.
 *
 * Money is shown beside the percentage on purpose. In a small field paying
 * three places, THIRD PRIZE IS SMALLER THAN THE BUY-IN — 12% of a four-man £20
 * pot is £2.40 against a £5 entry — and a bare "12%" hides that. Pinned by
 * `test_top_three_split_the_pot` on the server, which asserts third place ends
 * the day down.
 */
export const splitMoney = (places, pot, shares = null) => {
  const percents = shares || DEFAULT_SPLITS[places] || DEFAULT_SPLITS[1];
  return percents.map((percent, index) => ({
    place: index + 1,
    percent,
    amount: Math.round(pot * percent) / 100,
  }));
};

/** 1st, 2nd, 3rd … for naming a place. */
export const ordinal = (n) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10]) || 'th'}`;
};

/**
 * Are these shares a legal set of terms?
 *
 * Mirrors the server, which is still the guard: one share per paid place,
 * every place paying something (a 0% place is a place that is not paid, and
 * "top 3" where third gets nothing is what gets argued about in the bar), and
 * the whole pot handed out.
 */
export const splitsValid = (shares, places) => {
  if (!Array.isArray(shares) || shares.length !== places) return false;
  if (shares.some((n) => !(Number(n) > 0))) return false;
  const total = shares.reduce((sum, n) => sum + Number(n), 0);
  return Math.round(total * 1e6) / 1e6 === 100;
};

/**
 * How an open round names itself in the picker.
 *
 * A prediction on ONE ROUND and one on the WHOLE COMPETITION are different
 * bets, and the label is where that difference lives — so the overall says so
 * in words ("Overall · 3 rounds"), never by the absence of a round number.
 *
 * The round number is dropped for a one-round competition, where "Round 1 of 1"
 * is noise: a member reads the competition name and already knows.
 */
export const roundLabel = (round) => {
  if (!round) return '';
  const name = round.tournament_name || 'Competition';
  if (round.scope === 'overall') {
    return `${name} · Overall`;
  }
  if ((round.round_count || 1) <= 1) return name;
  return `${name} · Round ${round.round_number} of ${round.round_count}`;
};

/** The sub-line under the picker: what this scope actually covers. */
export const scopeNote = (round) => {
  if (!round) return '';
  if (round.scope === 'overall') {
    return `All ${round.round_count} rounds added together`;
  }
  if ((round.round_count || 1) > 1) {
    return `This round only · ${round.round_count} in the competition`;
  }
  return 'This round';
};

/** The value a <select> option carries — scope and round in one token. */
export const roundKey = (round) => (
  round.scope === 'overall'
    ? `${round.tournament_id}:overall`
    : `${round.tournament_id}:${round.round_number}`
);

/**
 * Who starts on side one, and whether that side can be changed.
 *
 * This rule has been wrong twice, so it lives here with tests on it rather than
 * inside a component. Both halves turn on the SAME question — is the caller
 * actually playing this round:
 *
 *   · seeded  — put the caller on side one only if they are in the field.
 *               An organiser who is not playing has no "you" to place, and
 *               seeding them anyway builds a prediction the server refuses with
 *               "not entered in this competition".
 *   · locked  — fix side one to the caller only if they are in the field AND
 *               may not name other players. Locking a side to somebody outside
 *               the field is a form that cannot succeed at all.
 */
export const sideOneSeed = ({ myPlayerId, meInField }) => (
  myPlayerId != null && meInField ? [myPlayerId] : []
);

export const sideOneLocked = ({ canLogForOthers, meInField }) => (
  !canLogForOthers && Boolean(meInField)
);

/** The three shapes a prediction can take, with the line that explains each. */
export const BET_TYPES = [
  { value: 'h2h', label: 'Head to head', blurb: 'One player against another' },
  { value: 'pairs', label: 'Pairs', blurb: 'Two against two' },
  { value: 'sweep', label: 'Sweep', blurb: 'Everyone in, best cards win' },
];

/** Human name for a competition format, for the round card's second line. */
export const FORMAT_DESCRIPTION = {
  'Pairs Matchplay': 'Pairs better-ball match play',
  'Singles Matchplay': 'Singles match play',
  'Stableford': 'Stableford',
  'Stroke Play': 'Stroke play',
};

/** "Pairs better-ball match play · 8 playing" */
/**
 * The second line of a round card: when, what and how many.
 *
 * THE DATE LIVES HERE, not in the title. The title ellipsises — it has to,
 * because a course name can be anything — and a date on the end of it is the
 * first thing a long name eats. Measured at 360px, "The Duke's Course,
 * Gleneagles · Sat 20 Sep" needed 325px against 231 available, so the day the
 * round is played vanished off the only card that says which round this is.
 * This line is short enough to carry it.
 */
export const roundDescription = (round) => {
  if (!round) return '';
  const format = FORMAT_DESCRIPTION[round.format] || round.format || 'Golf';
  const size = round.field_size;
  const when = formatRoundDate(round.date);
  return [when, format, size ? `${size} playing` : null]
    .filter(Boolean).join(' · ');
};

/** "Drift Golf Club · Sat 19 Sep", falling back to the competition's name. */
/** The first line of a round card: WHERE. See `roundDescription` for why
 *  nothing else may sit on the end of it. */
export const roundTitle = (round) => {
  if (!round) return '';
  return round.course_name || round.tournament_name || 'Round';
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Sat 19 Sep".
 *
 * Spelled out rather than left to `toLocaleDateString`, because the platform's
 * ICU decides that abbreviation and they disagree: Node says "Sept", browsers
 * say "Sep". This app runs in three runtimes — the website, an iOS WebView and
 * an Android WebView — and a round card that reads differently on a phone than
 * on a laptop is a bug nobody would think to look for.
 */
export const formatRoundDate = (iso) => {
  if (!iso) return '';
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${DAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

/**
 * Which bet type to start on, taken from what the round actually is.
 *
 * A pairs match play day is already drawn as pairs; a stroke or Stableford day
 * has a field and a leaderboard, which is what a sweep needs. Anything else
 * (singles match play included) is one against one.
 */
export const defaultBetType = (round) => {
  if (!round) return 'h2h';
  if (round.format === 'Pairs Matchplay') return 'pairs';
  if (round.format === 'Stableford' || round.format === 'Stroke Play') {
    return 'sweep';
  }
  return 'h2h';
};

/**
 * What a bet on this round is scored on, before the user touches anything.
 *
 * A SWEEP FOLLOWS THE DAY. It ranks a field, so it should rank it the way the
 * competition itself does — a Stableford day is won on points, and a sweep on
 * it settled by nett strokes would crown somebody the leaderboard did not.
 * Head to head and pairs default to nett and offer the Nett/Gross toggle; a
 * match is scored on the match and offers nothing.
 */
export const defaultScoring = (round, type) => {
  if (round?.is_matchplay) return 'matchplay';
  if (type === 'sweep') {
    return round?.format === 'Stableford' ? 'stableford' : 'nett';
  }
  return 'nett';
};

/**
 * Which bet types a round will actually accept.
 *
 * A match is conceded rather than completed, so its card is short and there is
 * no stroke total to rank — the server refuses anything but the match on a
 * matchplay round, and offering it here would only produce a 400 at the end.
 */
export const allowedBetTypes = (round) => {
  if (!round?.is_matchplay) return ['h2h', 'pairs', 'sweep'];
  return round.pairs_matchplay ? ['pairs'] : ['h2h'];
};

/**
 * The plain-English summary the sticky footer shows.
 *
 * FULL NAMES FOR A HEAD TO HEAD, first names for a pair. Measured: "Sam Tanner
 * v Baz Bailey · nett" fits the footer at 360px, but the same treatment over
 * four players ("Sam Tanner & Nick Brown v Baz Bailey & David Black · gross")
 * truncates at every phone width — and this line is the one that says what you
 * are about to confirm, so it must not end in an ellipsis.
 */
export const betSummary = ({ type, sides, entrants, format, nameOf }) => {
  if (type === 'sweep') {
    return entrants.length
      ? `${entrants.length} players · ${format}`
      : 'Nobody in yet';
  }
  const isPair = (sides[0] || []).length > 1 || (sides[1] || []).length > 1;
  const render = (id) => (isPair ? shortName(nameOf(id)) : nameOf(id));
  const side = (index) => (sides[index] || []).map(render).join(' & ');
  const one = side(0);
  const two = side(1);
  if (!one || !two) return null;
  return `${one} v ${two} · ${format}`;
};
